import React, { useState } from 'react';
import { bool, object, string } from 'prop-types';
import classNames from 'classnames';

import config from '../../../config';
import { FormattedMessage, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import getCountryCodes from '../../../translations/countryCodes';
import usStates from 'us-state-codes';
import {
  Button,
  FieldCheckbox,
  FieldSelect,
  FieldTextInput,
  IconCollection,
} from '../../../components';
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from 'react-accessible-accordion';

import css from './ShippingDetails.module.css';
import { useDispatch } from 'react-redux';
import { updateProfile } from '../../ProfileSettingsPage/ProfileSettingsPage.duck';
import { getVerifiedAddress } from '../CheckoutPage.duck';
import { addressVerification } from '../../../util/api';
import { v4 as uuidv4 } from 'uuid';
import { STEP_1 } from '../../../util/types';

const ShippingDetails = props => {
  const {
    rootClassName,
    className,
    intl,
    disabled,
    form,
    handleSameAddressCheckbox,
    editIndex,
    handleEditShippingData,
    fieldId,
    values,
    buyerDeliveryAddress,
    steps,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const dispatch = useDispatch();
  const [isNewAddress, setIsNewAddress] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProgress, setIsProgress] = useState(false);
  const optionalText = intl.formatMessage({
    id: 'ShippingDetails.optionalText',
  });

  // Use tha language set in config.locale to get the correct translations of the country names
  const countryCodes = getCountryCodes(config.locale);

  const handleShippingForm = () => {
    if (isNewAddress) {
      setIsNewAddress(false);
    } else {
      setIsNewAddress(true);
    }
  };
  const submitDisabled =
    !values.recipientCountry ||
    !values.recipientPhoneNumber ||
    !values.recipientCity ||
    !values.recipientState ||
    !values.recipientPostal ||
    !values.recipientAddressLine1 ||
    (!values.defaultAddress && !buyerDeliveryAddress.length > 0);

  const handleShippingDetails = async () => {
    const data = {
      address: values.recipientAddressLine1,
      city: values.recipientCity,
      state: values.recipientState,
      country: values.recipientCountry,
      postalCode: values.recipientPostal,
    };
    const shippingData = {
      userShippingAddressLine1: values.recipientAddressLine1,
      userShippingAddressLine2: values.recipientAddressLine2,
      userShippingCity: values.recipientCity,
      userShippingState: values.recipientState,
      userShippingCountry: values.recipientCountry,
      userShippingPostalCode: values.recipientPostal,
      defaultAddress: values.defaultAddress,
      userShippingPhoneNumber: values.recipientPhoneNumber,
      userShippingName: values.recipientName,
    };
    setIsProgress(true);
    const result = await addressVerification(data);
    if (result[0].status === 'verified') {
      if (values.defaultAddress) {
        // Create a copy of the sellerShippingAddress array with defaultAddress set to false for all addresses
        const updatedAddresses = buyerDeliveryAddress.map(address => ({
          ...address,
          defaultAddress: false,
        }));
        if (editIndex > -1) {
          updatedAddresses[editIndex] = { ...shippingData, defaultAddress: true };
        } else {
          // Add the new address with defaultAddress set to true
          updatedAddresses.push({ ...shippingData, defaultAddress: true });
        }

        const profile = {
          protectedData: {
            buyerDeliveryAddress: updatedAddresses,
          },
        };
        dispatch(updateProfile(profile)).then(() => {
          handleEditShippingData(-1);
          handleShippingForm();
          setIsProgress(false);
          setErrorMessage(result[0].messages[0]);
        });
      } else {
        if (editIndex > -1) {
          buyerDeliveryAddress[editIndex] = shippingData;
        } else {
          // Add the new address with defaultAddress set to true
          buyerDeliveryAddress.push(shippingData);
        }
        const profile = {
          protectedData: {
            buyerDeliveryAddress,
          },
        };
        dispatch(updateProfile(profile)).then(() => {
          handleShippingForm();
          handleEditShippingData(-1);
          setIsProgress(false);
          setErrorMessage(result[0].messages[0]);
        });
      }
    } else {
      setErrorMessage(result[0].messages[0]);
      setIsProgress(false);
    }
  };

  return (
    <>
      {steps == STEP_1 ? (
        <div>
          <div>
            <p className={css.shipTo}>
              <FormattedMessage id="ShippingDetails.shipTo" />
            </p>
            <div className={css.shippingAddressContent}>
              {buyerDeliveryAddress.length > 0 &&
                buyerDeliveryAddress.map((ship, index) => {
                  return (
                    <div className={css.shippingAddressItem} key={uuidv4()}>
                      <div
                        className={css.ShippingAddresstext}
                        onClick={() => {
                          const updatedArray = buyerDeliveryAddress.map((item, i) => ({
                            ...item,
                            defaultAddress: i === index ? true : false,
                          }));
                          const profile = {
                            protectedData: {
                              buyerDeliveryAddress: updatedArray,
                            },
                          };
                          dispatch(updateProfile(profile));
                        }}
                      >
                        {ship.defaultAddress ? (
                          <IconCollection icon="selected" />
                        ) : (
                          <IconCollection icon="unSelected" />
                        )}
                        <IconCollection icon="IconLocation" />
                        <span>
                          <span>{ship.userShippingAddressLine1}</span>,
                          <span>{ship.userShippingCity}</span>,<span>{ship.userShippingState}</span>
                          ,<span>{ship.userShippingCountry}</span> |{' '}
                          <span>{ship.userShippingPostalCode}</span>
                        </span>
                      </div>
                      <div className={css.actionButtons}>
                        <span
                          onClick={() => {
                            buyerDeliveryAddress.splice(index, 1);
                            const profile = {
                              protectedData: {
                                buyerDeliveryAddress,
                              },
                            };
                            dispatch(updateProfile(profile));
                            handleEditShippingData(-1);
                          }}
                        >
                          {' '}
                          <IconCollection icon="IconDelete" />
                        </span>
                        <span
                          onClick={() => {
                            handleEditShippingData(index);
                            handleShippingForm();
                          }}
                          className={css.iconEdit}
                        >
                          {' '}
                          <IconCollection icon="IconEdit" />
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
            <br />
          </div>
          {isNewAddress && (
            <div>
              <h3 className={css.heading}>
                <FormattedMessage id="ShippingDetails.newTitle" />
              </h3>
              <br />
              {errorMessage && errorMessage.type === 'warning' && (
                <span className={css.warning}>
                  <FormattedMessage id="ShippingDetails.warningText" />{' '}
                </span>
              )}
              <span
                className={
                  errorMessage && errorMessage.type === 'warning' ? css.warning : css.errorText
                }
              >
                {errorMessage && errorMessage.message}
              </span>
              <FieldTextInput
                id={`${fieldId}.recipientName`}
                name="recipientName"
                disabled={disabled}
                className={css.fieldFullWidth}
                type="text"
                autoComplete="shipping name"
                label={intl.formatMessage({ id: 'ShippingDetails.recipientNameLabel' })}
                placeholder={intl.formatMessage({
                  id: 'ShippingDetails.recipientNamePlaceholder',
                })}
                validate={validators.required(
                  intl.formatMessage({ id: 'ShippingDetails.recipientNameRequired' })
                )}
                onUnmount={() => form.change('recipientName', undefined)}
              />
              <FieldTextInput
                id={`${fieldId}.recipientPhoneNumber`}
                name="recipientPhoneNumber"
                disabled={disabled}
                className={css.fieldFullWidth}
                type="text"
                autoComplete="shipping phoneNumber"
                label={intl.formatMessage(
                  { id: 'ShippingDetails.recipientPhoneNumberLabel' }
                  // { optionalText: optionalText }
                )}
                placeholder={intl.formatMessage({
                  id: 'ShippingDetails.recipientPhoneNumberPlaceholder',
                })}
                validate={validators.required(
                  intl.formatMessage({ id: 'ShippingDetails.recipientPhoneNumberRequired' })
                )}
                onUnmount={() => form.change('recipientPhoneNumber', undefined)}
              />
              <div className={css.formRow}>
                <FieldTextInput
                  id={`${fieldId}.recipientAddressLine1`}
                  name="recipientAddressLine1"
                  disabled={disabled}
                  className={css.field}
                  type="text"
                  autoComplete="shipping address-line1"
                  label={intl.formatMessage({ id: 'ShippingDetails.addressLine1Label' })}
                  placeholder={intl.formatMessage({
                    id: 'ShippingDetails.addressLine1Placeholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({ id: 'ShippingDetails.addressLine1Required' })
                  )}
                  onUnmount={() => form.change('recipientAddressLine1', undefined)}
                />

                <FieldTextInput
                  id={`${fieldId}.recipientAddressLine2`}
                  name="recipientAddressLine2"
                  disabled={disabled}
                  className={css.field}
                  type="text"
                  autoComplete="shipping address-line2"
                  label={intl.formatMessage(
                    { id: 'ShippingDetails.addressLine2Label' },
                    { optionalText: optionalText }
                  )}
                  placeholder={intl.formatMessage({
                    id: 'ShippingDetails.addressLine2Placeholder',
                  })}
                  onUnmount={() => form.change('recipientAddressLine2', undefined)}
                />
              </div>
              <div className={css.formRow}>
                <FieldTextInput
                  id={`${fieldId}.recipientPostalCode`}
                  name="recipientPostal"
                  disabled={disabled}
                  className={css.field}
                  type="text"
                  autoComplete="shipping postal-code"
                  label={intl.formatMessage({ id: 'ShippingDetails.postalCodeLabel' })}
                  placeholder={intl.formatMessage({
                    id: 'ShippingDetails.postalCodePlaceholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({ id: 'ShippingDetails.postalCodeRequired' })
                  )}
                  onUnmount={() => form.change('recipientPostal', undefined)}
                />

                <FieldTextInput
                  id={`${fieldId}.recipientCity`}
                  name="recipientCity"
                  disabled={disabled}
                  className={css.field}
                  type="text"
                  autoComplete="shipping address-level2"
                  label={intl.formatMessage({ id: 'ShippingDetails.cityLabel' })}
                  placeholder={intl.formatMessage({ id: 'ShippingDetails.cityPlaceholder' })}
                  validate={validators.required(
                    intl.formatMessage({ id: 'ShippingDetails.cityRequired' })
                  )}
                  onUnmount={() => form.change('recipientCity', undefined)}
                />
              </div>
              <div className={css.formRow}>
                <FieldTextInput
                  id={`${fieldId}.recipientState`}
                  name="recipientState"
                  disabled={disabled}
                  className={css.field}
                  type="text"
                  autoComplete="shipping address-level1"
                  label={intl.formatMessage(
                    { id: 'ShippingDetails.stateLabel' }
                    // { optionalText: optionalText }
                  )}
                  placeholder={intl.formatMessage({ id: 'ShippingDetails.statePlaceholder' })}
                  onUnmount={() => form.change('recipientState', undefined)}
                />

                <FieldSelect
                  id={`${fieldId}.recipientCountry`}
                  name="recipientCountry"
                  disabled={disabled}
                  className={css.field}
                  label={intl.formatMessage({ id: 'ShippingDetails.countryLabel' })}
                  validate={validators.required(
                    intl.formatMessage({ id: 'ShippingDetails.countryRequired' })
                  )}
                >
                  <option disabled value="">
                    {intl.formatMessage({ id: 'ShippingDetails.countryPlaceholder' })}
                  </option>
                  {/* {countryCodes.map(country => {
                    return ( */}
                  <option value={'US'}>{'United States'}</option>
                  {/* );
                  })} */}
                </FieldSelect>
              </div>
              <FieldCheckbox
                id="defaultAddress"
                name="defaultAddress"
                className={classNames(css.sameAddressCheckbox, css.checkBoxRight)}
                label={intl.formatMessage({ id: 'ShippingDetails.defaultAddressLabel' })}
              />
            </div>
          )}
          {isNewAddress ? (
            <Button
              type="button"
              disabled={submitDisabled}
              className={css.addressAddBtn}
              inProgress={isProgress}
              onClick={handleShippingDetails}
            >
              <IconCollection icon="IconLocation" />
              <FormattedMessage id="ShippingLabelForm.saveButton" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => {
                handleShippingForm();
              }}
              className={css.addressAddBtn}
            >
              <IconCollection icon="IconLocation" />
              <FormattedMessage id="ShippingLabelForm.addNewButton" />
            </Button>
          )}
        </div>
      ) : null}
    </>
  );
};

ShippingDetails.defaultProps = {
  rootClassName: null,
  className: null,
  disabled: false,
  fieldId: null,
};

ShippingDetails.propTypes = {
  rootClassName: string,
  className: string,
  disabled: bool,
  form: object.isRequired,
  fieldId: string,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default ShippingDetails;
